import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Activity, Step} from 'APIs/letrusAPI/activities/types';
import StepBullet, {StepStatus} from '../StepBullet';
import styles from './ActivityStepper.module.scss';
import getFocusModeCardState from '../DashboardCard/focusModeUtils';

export interface ActivityStepperProps {
  activity: Activity;
}

function ActivityStepper({activity}: ActivityStepperProps): JSX.Element {
  const [steps, setSteps] = useState(activity.steps);
  const navigate = useNavigate();
  const {navigateToLearningPath} = useLearningPathNavigation(activity);
  const singleStepStyle = steps?.length === 1 ? 'singleStep' : '';
  const isLearningPathActivity = activity.activity_type === 'learning_path';
  const isTestActivity = activity.activity_type === 'test';

  useEffect(() => {
    if (isTestActivity) {
      setSteps([
        {
          order: 1,
          available: true,
          id: activity.id,
          activity_type: 'write',
          composition_status: activity.composition_status,
        },
      ]);
    }
  }, []);

  function getBulletStatus(step: Step): StepStatus {
    const writeCompositionStatus =
      activity?.composition_status ?? activity.status;

    const originalStatus: StepStatus = isLearningPathActivity
      ? getStepBulletStatus(step)
      : getWriteBulletStatus(writeCompositionStatus);

    // Apply focus-mode display overrides: if the activity is expired but the
    // focus-mode util indicates this step has progress, surface it as
    // `finished` so the bullet becomes clickable/accessible.
    const fmState = getFocusModeCardState(activity);
    const stepHasHighlight = fmState.stepHighlightPredicate
      ? fmState.stepHighlightPredicate(step.order)
      : false;

    if (
      (originalStatus === 'expired' || originalStatus === 'scheduled') &&
      stepHasHighlight
    ) {
      return 'finished';
    }

    return originalStatus;
  }

  function getWriteBulletStatus(compositionStatus?: string): StepStatus {
    const WRITE_BULLET_STATUS = {
      scheduled: 'scheduled',
      expired: 'expired',
      opened: 'available',
      reviewed: 'finished',
      awaiting: 'scheduled',
      writing: 'inProgress',
      preparing: 'inProgress',
      in_progress: 'inProgress',
      finished: 'finished',
      'manual review pending': 'inReview',
      'automatic review running': 'inReview',
      'teacher revision pending': 'inReview',
    };

    return WRITE_BULLET_STATUS[compositionStatus ?? ''];
  }

  function getWriteStepBulletStatus(step: Step): StepStatus {
    const isCurrentStep = step.order === activity.step_number;
    const isWriteOpened = !step?.activity_status && isCurrentStep;
    const isLearningPathInProgress = activity.status === 'in_progress';
    const stepCompositionStatus = isWriteOpened
      ? 'opened'
      : step?.activity_status;

    const bulletStatus = getWriteBulletStatus(stepCompositionStatus);
    const hasWriteFeedback = bulletStatus?.match(/finished|inReview/);

    if (isLearningPathInProgress || hasWriteFeedback) {
      return bulletStatus;
    }

    return activity.status as StepStatus;
  }

  function getStepBulletStatus(step: Step): StepStatus {
    const isPreviousStep = step.order < activity.step_number!;
    const currentStepStatus =
      step.order === activity.step_number ? 'inProgress' : '';

    const LEARNING_PATH_BULLET_STATUS = {
      expired: isPreviousStep ? 'finished' : 'expired',
      finished: isPreviousStep ? 'finished' : 'finished',
      scheduled: isPreviousStep ? 'finished' : 'scheduled',
      in_progress: isPreviousStep ? 'finished' : currentStepStatus,
    };

    return step.activity_type === 'write'
      ? getWriteStepBulletStatus(step)
      : LEARNING_PATH_BULLET_STATUS[activity.status];
  }

  function redirectToWriteActivity() {
    const isStepOpened = activity?.status === 'opened';
    const pathname = isStepOpened ? '/criar' : '';
    const routeId = isStepOpened ? activity?.id : activity?.composition_id;

    navigate(`/atividades/escrita/${routeId}${pathname}`);
  }

  function handleStepBulletClick(stepOrder: number) {
    if (isLearningPathActivity) {
      // If this is a Focus Mode learning path that hasn't been started,
      // require the user to start the trilha via the instruction modal.
      if (activity?.uses_focus_mode && !activity?.started) {
        // Block navigation silently; start must happen via the start button.
        return;
      }

      navigateToLearningPath.stepByOrder(stepOrder);
    } else {
      redirectToWriteActivity();
    }
  }

  const getWriteSteps =
    activity?.steps?.filter((step) => step.activity_type === 'write') ?? [];

  const shouldShowSentCaption =
    activity.uses_focus_mode &&
    activity.feedback_released === false &&
    (activity.status === 'finished' || activity.status === 'expired');

  function getStepCaption(step: Step, status: StepStatus) {
    if (
      shouldShowSentCaption &&
      step.activity_type === 'write' &&
      step.activity_id != null &&
      status === 'finished'
    ) {
      return 'Escrita Enviada';
    }

    return undefined;
  }

  return (
    <ul
      className={`${styles.wrapper} ${styles[singleStepStyle]}`}
      data-testid="activity-stepper"
      aria-label="Etapas da trilha"
    >
      {steps?.map((step) => {
        const bulletStatus = getBulletStatus(step);

        return (
          <li key={step.order}>
            <StepBullet
              order={step.order}
              type={
                step.activity_type === 'write' &&
                getWriteSteps?.length > 1 &&
                getWriteSteps[0].order < step.order
                  ? 'rewrite'
                  : step.activity_type
              }
              status={bulletStatus}
              captionOverride={getStepCaption(step, bulletStatus)}
              onClick={() => handleStepBulletClick(step.order)}
              isOlympics={activity.name === 'Olimpíada de Redação Letrus'}
            />
          </li>
        );
      })}
    </ul>
  );
}

export default ActivityStepper;
